import './SaturationCurveChart.scss';
import { ReactElement, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Brand60,
  Danger400,
  Neutral300,
  Neutral500,
  Neutral600,
  Success500,
  Neutral100
} from '../../../utils/colors';

interface ChartPoint {
  spend: number;
  value: number | null;
  band?: [number, number];
}

interface CurveTooltipProps {
  // Injected by recharts when passed as the Tooltip `content` element.
  active?: boolean;
  payload?: { payload?: ChartPoint }[];
  label?: number;
  xFormatter: (val: number) => string;
  yFormatter: (val: number) => string;
  yLabel: string;
}

const CurveTooltip = ({
  active,
  payload,
  label,
  xFormatter,
  yFormatter,
  yLabel
}: CurveTooltipProps): ReactElement | null => {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }
  return (
    <div className='bb-chart-tooltip'>
      <div className='bb-chart-tooltip-row'>
        <span>Weekly Spend</span>
        <strong>{xFormatter(Number(label))}</strong>
      </div>
      <div className='bb-chart-tooltip-row'>
        <span>{yLabel}</span>
        <strong>{row.value != null ? yFormatter(row.value) : '--'}</strong>
      </div>
      {row.band && (
        <div className='bb-chart-tooltip-ci'>
          CI: {yFormatter(row.band[0])} – {yFormatter(row.band[1])}
        </div>
      )}
    </div>
  );
};

interface SaturationCurveChartProps {
  points: ChartPoint[];
  activeIndex: number;
  onSelectIndex: (index: number) => void;
  xFormatter: (val: number) => string;
  /** Precise formatter for the tooltip. */
  yFormatter: (val: number) => string;
  /** Compact formatter for axis ticks (keeps wide values from crowding the axis title). */
  yTickFormatter: (val: number) => string;
  yLabel: string;
  /** Primary-budget weekly spend; draws a vertical reference line when provided. */
  primaryX?: number;
  /** Draws a horizontal break-even line at y = 1 (marginal ROAS = 1). */
  showBreakeven?: boolean;
  color?: string;
  height?: number;
}

// The Y axis is linear and scaled to fit the FULL CI band (the lower bound
// floors at 0, the upper bound sits just above the curve), so the whole band
// renders with crisp edges and no fade/clip. QA gates pathological groups out
// upstream, so the band stays within ~1.6× of the curve and never squashes it.
const BAND_HEADROOM = 1.05;

export const SaturationCurveChart = ({
  points,
  activeIndex,
  onSelectIndex,
  xFormatter,
  yFormatter,
  yTickFormatter,
  yLabel,
  primaryX,
  showBreakeven,
  // Curve + CI band are brand purple (matches the Scaling page); the selected
  // point is green to echo the sliders (Success500 / --bb-accent).
  color = Brand60,
  height = 400
}: SaturationCurveChartProps): ReactElement => {
  const [dragging, setDragging] = useState(false);
  const active = points[activeIndex];

  const selectIndex = (idx: unknown): void => {
    const n = Number(idx);
    if (Number.isInteger(n)) {
      onSelectIndex(n);
    }
  };

  // Axis top = the highest curve point OR CI band, plus headroom. Bottom is 0
  // for the outcome/efficiency plots, but the marginal plot can go negative past
  // saturation, so the floor drops below 0 when the data does — otherwise the
  // most decision-relevant region (marginal ROAS crossing 1, then negative)
  // would be clamped flat against the axis.
  const { yMin, yMax } = useMemo(() => {
    const vals: number[] = [];
    points.forEach(p => {
      if (p.value != null) {
        vals.push(p.value);
      }
      if (p.band) {
        vals.push(p.band[0], p.band[1]);
      }
    });
    if (!vals.length) {
      return { yMin: 0, yMax: undefined as number | undefined };
    }
    return { yMin: Math.min(0, ...vals), yMax: Math.max(...vals) * BAND_HEADROOM };
  }, [points]);

  // Clamp the band to the axis bounds as a safety net; the tooltip always reports
  // the real CI.
  const data = useMemo(() => {
    const clampY = (v: number): number => (yMax == null ? v : Math.min(Math.max(v, yMin), yMax));
    return points.map(p => ({
      ...p,
      bandPlot: p.band ? ([clampY(p.band[0]), clampY(p.band[1])] as [number, number]) : undefined
    }));
  }, [points, yMin, yMax]);

  return (
    <ResponsiveContainer width='100%' height={height}>
      <ComposedChart
        data={data}
        margin={{ top: 24, right: 16, bottom: 28, left: 12 }}
        onMouseDown={state => {
          setDragging(true);
          selectIndex(state?.activeTooltipIndex);
        }}
        onMouseMove={state => {
          if (dragging) {
            selectIndex(state?.activeTooltipIndex);
          }
        }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
      >
        <CartesianGrid stroke={Neutral300} strokeOpacity={0.6} vertical={false} />
        <XAxis
          dataKey='spend'
          type='number'
          domain={['dataMin', 'dataMax']}
          tickFormatter={xFormatter}
          tick={{ fontSize: 14, fill: Neutral500 }}
          tickLine={false}
          height={48}
          label={{
            value: 'Weekly Spend',
            position: 'insideBottom',
            offset: 0,
            style: { fontSize: 14, fill: Neutral600 }
          }}
        />
        <YAxis
          domain={[yMin, yMax ?? 'auto']}
          allowDataOverflow
          tickFormatter={yTickFormatter}
          tick={{ fontSize: 14, fill: Neutral500 }}
          tickLine={false}
          axisLine={false}
          width={80}
          label={{
            value: yLabel,
            angle: -90,
            position: 'insideLeft',
            dx: -4,
            style: { fontSize: 14, fill: Neutral600, textAnchor: 'middle' }
          }}
        />
        <Tooltip
          isAnimationActive={false}
          cursor={{ stroke: Neutral500, strokeDasharray: '3 3' }}
          content={<CurveTooltip xFormatter={xFormatter} yFormatter={yFormatter} yLabel={yLabel} />}
        />
        <Area
          dataKey='bandPlot'
          stroke='none'
          fill={color}
          fillOpacity={0.14}
          isAnimationActive={false}
          connectNulls
        />
        {primaryX != null && (
          <ReferenceLine
            x={primaryX}
            stroke={Neutral500}
            strokeDasharray='4 4'
            label={{ value: 'Primary', position: 'top', fontSize: 13, fill: Neutral600 }}
          />
        )}
        {showBreakeven && (
          <ReferenceLine
            y={1}
            stroke={Danger400}
            strokeDasharray='4 4'
            label={{
              value: 'Breakeven',
              position: 'insideTopRight',
              fontSize: 13,
              fill: Danger400
            }}
          />
        )}
        <Line
          dataKey='value'
          stroke={color}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
          connectNulls
        />
        {active?.value != null && (
          <ReferenceDot
            x={active.spend}
            y={active.value}
            r={6}
            fill={Success500}
            stroke={Neutral100}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
